import { MEDIUM_MODEL, LIGHTWEIGHT_MODEL } from "@/lib/ai/models";
import { Client, Deal } from "@/app/(app)/deals/create/page";
import { Theme } from "@glideapps/glide-data-grid";
import type { DefaultMetric } from "./types";

export const RETRIEVAL_INSTRUCTION = "search_document"; // There are only four instructions in nomic and for retrieval embedding, they recommend "search_document"
export const QUESTION_RETRIEVAL_INSTRUCTION = "search_document";
export const QUERY_INSTRUCTION = "search_query";
export const COMPARE_INSTRUCTION = "Represent the sentence for comparison: ";
export const GET_DOCUMENTS_FN = "get_documents";
export const SQL_FN = "sql";
export const SEARCH_DOCUMENT_CHUNKS_FN = "search_knowledge_base";
export const GET_DEAL_FN = "get_deal";
// Native (non-CRM) deal management — for orgs without HubSpot/Salesforce connected.
export const CREATE_DEAL_FN = "create_deal";
export const UPDATE_DEAL_FN = "update_deal";
// Stage progression uses CRM write permissions for integrated deals.
export const UPDATE_DEAL_STAGE_FN = "update_deal_stage";
export const ADD_DEAL_CONTACTS_FN = "add_deal_contacts";
export const UPLOAD_DEAL_DOCUMENT_FN = "upload_deal_document";
export const LIST_CALENDAR_MEETINGS_FN = "list_calendar_meetings";
export const SEARCH_GMAIL_THREADS_FN = "search_gmail_threads";
export const INGEST_GMAIL_THREAD_FN = "ingest_gmail_thread";
export const LIST_PLAY_TIERS_FN = "list_play_tiers";
export const LIST_PLAY_OPPORTUNITIES_FN = "list_play_opportunities";
export const ENROLL_DEAL_IN_PLAY_FN = "enroll_deal_in_play";
export const ENROLL_CALENDAR_EVENT_IN_PLAY_FN = "enroll_calendar_event_in_play";
export const UPSERT_CRM_CLIENT_FN = "upsert_crm_backed_client";
export const LOOKUP_CRM_ACCOUNT_FN = "lookup_crm_account";
export const ENROLL_PLAYS_BATCH_FN = "enroll_plays_batch";
export const GET_OR_GENERATE_COMPARABLE_EVALUATION_FN = "get_or_generate_comparable_evaluation";
// Read-only comparable deal retrieval. Unlike get_or_generate_comparable_evaluation,
// this never starts research or persists recommendations.
export const SEARCH_COMPARABLE_DEALS_FN = "search_comparable_deals";
export const END_SURVEY_FN = "end_survey";
export const END_ROLEPLAY_FN = "end_roleplay";
export const ANALYZE_DEALGRID_SUBSET_FN = "analyze_dealgrid_subset";
export const GET_PIPELINE_ANALYTICS_FN = "get_pipeline_analytics";
export const PULL_SCHEMA_FN = "pull_schema";
export const CREATE_DATASET_FN = "create_dataset";
export const RUN_SANDBOX_DATASET_ANALYSIS_FN = "run_sandbox_dataset_analysis";
export const LOAD_AGENT_SKILL_FN = "load_agent_skill";
export const GET_REPORT_TEMPLATE_FN = "get_report_template";
export const CREATE_HTML_REPORT_FN = "create_html_report";
export const LIST_PAGE_TEMPLATES_FN = "list_page_templates";
export const GET_PAGE_TEMPLATE_FN = "get_page_template";
export const CREATE_HTML_PAGE_FN = "create_html_page";
export const SEARCH_DEALS_FN = "select_deal";
export const SEARCH_ACROSS_DEALS_FN = "search_across_deals";
export const SEMANTIC_DEAL_SEARCH_FN = "search_deals";
export const SEARCH_COMPETITORS_FN = "search_competitors"; // Legacy - kept for backwards compatibility
export const SEARCH_COMPETITIVE_ASSETS_FN = "search_competitive_assets"; // New: searches type="asset" documents
export const SEARCH_COMPETITIVE_INTEL_FN = "search_competitive_intel"; // Searches pre-saved intel (type IS NULL)
export const SEARCH_WEB_FN = "search_web";
export const SCRAPE_URL_FN = "scrape_url";
export const MAP_SITE_URLS_FN = "map_site_urls";
export const SEARCH_DEAL_DOCUMENTS_FN = "search_deal_documents";
export const SUMMARIZE_CONVERSATION_FN = "summarize_conversation";
export const PLAN_RESEARCH_FN = "plan_research";

// Win-Loss Scheduling Agent tools
export const CHECK_INTERVIEW_REQUESTS_FN = "check_interview_requests";
export const SEND_APPROVAL_REQUEST_FN = "send_approval_request";
export const SEND_INTERVIEW_REQUEST_FN = "send_interview_request";
export const SCHEDULE_FOLLOWUP_CHECK_FN = "schedule_followup_check";

// Azure OpenAI deployment names for the voice survey realtime pipeline.
// These are *deployment* names configured in Azure, not raw model IDs.
export const REALTIME_DEPLOYMENT = "gpt-realtime-1.5";
// Ordered fallback deployments tried (in order) if the primary fails to mint a session
// — e.g. Azure rolls a model version that breaks, or the primary is unavailable.
// Each entry must be a deployment that EXISTS in your Azure resource; non-existent ones
// simply fail and are skipped. All gpt-realtime* models share the same GA protocol, so
// switching between them needs no code change. NOTE: this guards per-model failures only,
// not API-version/auth failures (those fail identically for every candidate).
export const REALTIME_DEPLOYMENT_FALLBACKS = ["gpt-realtime-2", "gpt-realtime"];
export const REALTIME_TRANSCRIPTION_DEPLOYMENT = "whisper-1";

// Deal analysis orchestration tools
export const READ_DEAL_MD_FN = "read_deal_md";
export const WRITE_DEAL_MD_FN = "write_deal_md";
export const INSERT_TO_DEAL_MD_FN = "insert_to_deal_md";
export const STR_REPLACE_DEAL_MD_FN = "str_replace_deal_md";
export const SPAWN_DEAL_SUB_AGENTS_FN = "spawn_agents";

// Sub-agent spawn tools
export const SPAWN_DATA_ANALYSIS_AGENT_FN = "spawn_data_analysis_agent";
export const SPAWN_COMPETITIVE_INTEL_AGENT_FN = "spawn_competitive_intel_agent";
export const SPAWN_DEAL_RESEARCH_AGENT_FN = "spawn_deal_research_agent";

// Deal sub-agent investigation tools
export const LIST_DEAL_DOCUMENTS_FN = "list_deal_documents";
export const SEARCH_CHUNKS_SEMANTIC_FN = "search_chunks_semantic";
export const GREP_DOCUMENT_FN = "grep_document";
export const PULL_FULL_DOCUMENT_FN = "pull_full_document";
export const RETURN_DEAL_RESULTS_FN = "return_results";

// Insight item agent terminal tools (per-metric / per-answer agentic runs)
export const SUBMIT_METRIC_SCORE_FN = "submit_metric_score";
export const SUBMIT_ANSWER_FN = "submit_answer";
export const SUBMIT_METRIC_GROUP_SCORES_FN = "submit_metric_group_scores";
export const SUBMIT_ANSWER_GROUP_FN = "submit_answer_group";
export const SUBMIT_PLAYBOOK_SECTION_VERSION_FN = "submit_playbook_section_version";
export const READ_PLAYBOOK_CONTEXT_FN = "read_playbook_context";

// Conversation management constants
export const AUTO_SUMMARIZE_THRESHOLD = 80000; // Token threshold to trigger auto-summarization
export const RECENT_TURNS_TO_PRESERVE = 3; // Number of recent turns to keep in full after summarization

export const VALIDATE_SOURCES_FN = "validate_sources";
export const DEAL_INFO_FN = "search_deal_info";
export const GET_TEMPLATE_FN = "get_template";
export const REVISE_FN = "revise";
export const DRAFT_FN = "draft";
export const DRAFT_EMAIL_FN = "draft_email";
export const DRAFT_RFP_FN = "draft_rfp";
export const DRAFT_SQ_FN = "draft_sq";
export const CUSTOMIZE_TEMPLATE_FN = "customize_template";
export const GENERATE_DOCUMENT_FN = "generate_document";
export const HUBSPOT_FN = "query_hubspot";
export const OPENAI_MODEL = MEDIUM_MODEL;
export const OPENAI_MINI_MODEL = LIGHTWEIGHT_MODEL;
export const CLAUDE_MODEL = "claude-3-5-sonnet-20240620";

// A generic message where embedding is compared to check if Paige doesn't know the answer.
export const UNSURE_MESSAGES = [
  "I couldn't find any specific information",
  "No answers could be found",
];

// Clerk Org Roles
export const CLERK_ADMIN = "admin";
export const CLERK_MEMBER = "basic_member";
// Role checkers: handle both legacy ("admin") and namespaced ("org:admin") formats across Clerk instances
export const isClerkAdmin = (role: string | null | undefined) =>
  role === "admin" || role === "org:admin";
export const isClerkMember = (role: string | null | undefined) =>
  role === "basic_member" || role === "org:basic_member" || isClerkAdmin(role);

// Mime Types
export const PDF_MIME_TYPE = "application/pdf";
export const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const PPTX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
export const OFFICE_MIME_TYPES = [
  XLSX_MIME_TYPE,
  PPTX_MIME_TYPE,
  DOCX_MIME_TYPE,
];
export const PLAIN_TEXT_MIME_TYPE = "text/plain";
export const CSV_MIME_TYPE = "text/csv";
export const MARKDOWN_MIME_TYPE = "text/markdown";
export const JPEG_MIME_TYPE = "image/jpeg";
export const PNG_MIME_TYPE = "image/png";
export const GOOGLE_DOCS_MIME_TYPE = "application/vnd.google-apps.document";
export const GOOGLE_SHEETS_MIME_TYPE =
  "application/vnd.google-apps.spreadsheet";
export const GOOGLE_SLIDES_MIME_TYPE =
  "application/vnd.google-apps.presentation";
export const GOOGLE_FOLDER_MIME_TYPE = "application/vnd.google-apps.folder";
export const MP4_MIME_TYPE = "video/mp4";
export const AVI_MIME_TYPE = "video/x-msvideo";
export const MOV_MIME_TYPE = "video/quicktime";
export const EXTERNAL_URL_MIME_TYPE = "external_link";
export const YOUTUBE_VIDEO_MIME_TYPE = "Youtube";
export const LOOM_MIME_TYPE = "Loom";

export const SALESFORCE_DTYPES = [
  "string",
  "boolean",
  "int",
  "double",
  "date",
  "datetime",
  "base64",
  "ID",
  "reference",
  "currency",
  "textarea",
  "percent",
  "phone",
  "url",
  "email",
  "combobox",
  "picklist",
  "multipicklist",
  "anyType",
  "location",
];
export const HUBSPOT_DTYPES = [
  "bool",
  "enumeration",
  "date",
  "dateTime",
  "string",
  "number",
  "object_coordinates",
  "json",
];

export const HUBSPOT_DATE_DTYPES = ["date", "dateTime"];
export const SALESFORCE_DATE_DTYPES = ["date", "datetime"];

export const SALESFORCE_DTYPE_TO_TS_TYPE: Record<string, string> = {
  string: "string",
  boolean: "boolean",
  int: "number",
  double: "number",
  date: "string",
  datetime: "string",
  // base64: "string", // We dont want to filter by base64
  // ID: "string", // we dont want to filter by ID
  // reference: "string", // Not sure what might be the best type for this
  currency: "number",
  textarea: "string",
  percent: "number",
  phone: "string",
  url: "string",
  email: "string",
  combobox: "string",
  picklist: "string",
  multipicklist: "string",
  // anyType: "string", // Not sure what might be the best type for this
  location: "string",
};
export const HUBSPOT_DTYPE_TO_TS_TYPE: Record<string, string> = {
  bool: "boolean",
  enumeration: "string",
  date: "string",
  dateTime: "string",
  string: "string",
  number: "number",
  // object_coordinates: "string", // Not sure what might be the best type for this
  // json: "string", // Not sure what might be the best type for this
};

export const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  [PDF_MIME_TYPE]: "pdf",
  [DOCX_MIME_TYPE]: "docx",
  [XLSX_MIME_TYPE]: "xlsx",
  [PPTX_MIME_TYPE]: "pptx",
  [PLAIN_TEXT_MIME_TYPE]: "txt",
  [CSV_MIME_TYPE]: "csv",
  [MARKDOWN_MIME_TYPE]: "md",
  [JPEG_MIME_TYPE]: "jpeg",
  [PNG_MIME_TYPE]: "png",
};

export const EXTENSION_TO_ONLYOFFICE_DOCUMENT_TYPE: Record<string, string> = {
  pdf: "pdf",
  docx: "word",
  xlsx: "cell",
  pptx: "slide",
  txt: "word",
};

export const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  pdf: PDF_MIME_TYPE,
  docx: DOCX_MIME_TYPE,
  xlsx: XLSX_MIME_TYPE,
  pptx: PPTX_MIME_TYPE,
  txt: PLAIN_TEXT_MIME_TYPE,
  csv: CSV_MIME_TYPE,
  md: MARKDOWN_MIME_TYPE,
  jpeg: JPEG_MIME_TYPE,
  png: PNG_MIME_TYPE,
};

export const VIDEO_MIME_TYPES = [
  MP4_MIME_TYPE,
  AVI_MIME_TYPE,
  MOV_MIME_TYPE,
  YOUTUBE_VIDEO_MIME_TYPE,
];

export const IMAGE_MIME_TYPES = [JPEG_MIME_TYPE, PNG_MIME_TYPE];

// Dangerous MIME types that should be blocked to prevent XSS
export const DANGEROUS_MIME_TYPES = [
  "text/html",
  "application/xhtml+xml",
  "application/javascript",
  "text/javascript",
  "application/x-javascript",
  "text/vbscript",
  "application/x-shockwave-flash",
  "application/x-silverlight-app",
  "text/xml",
  "application/xml",
];

// Allowed MIME types for uploads
export const ALLOWED_MIME_TYPES = [
  PDF_MIME_TYPE,
  DOCX_MIME_TYPE,
  XLSX_MIME_TYPE,
  PPTX_MIME_TYPE,
  PLAIN_TEXT_MIME_TYPE,
  CSV_MIME_TYPE,
  MARKDOWN_MIME_TYPE,
  JPEG_MIME_TYPE,
  PNG_MIME_TYPE,
  MP4_MIME_TYPE,
  AVI_MIME_TYPE,
  MOV_MIME_TYPE,
  ...OFFICE_MIME_TYPES,
];
// Deal Status
export const DEAL_CREATED_STATUS = "Deal Opened";
export const QUALIFIED_STATUS = "Qualified";
export const PRE_SALES_STATUS = "Pre-Sales";
export const WON_STATUS = "Won";
export const ONBOARDING_STATUS = "Onboarding";
export const POST_SALES_STATUS = "Support";
export const LOST_STATUS = "Lost";

export const DEAL_CREATED_ID = "dealopened";
export const QUALIFIED_ID = "qualified";
export const PRE_SALES_ID = "presales";
export const WON_ID = "won";
export const ONBOARDING_ID = "onboarding";
export const POST_SALES_ID = "postsales";
export const LOST_ID = "lost";

export const DEAL_STATUS_TO_ID: Record<string, string> = {
  [DEAL_CREATED_STATUS]: DEAL_CREATED_ID,
  [QUALIFIED_STATUS]: QUALIFIED_ID,
  [PRE_SALES_STATUS]: PRE_SALES_ID,
  [WON_STATUS]: WON_ID,
  [ONBOARDING_STATUS]: ONBOARDING_ID,
  [POST_SALES_STATUS]: POST_SALES_ID,
  [LOST_STATUS]: LOST_ID,
};

export const STATUS_TO_NUMBER: Record<string, number> = {
  [DEAL_CREATED_STATUS]: 1,
  [QUALIFIED_STATUS]: 2,
  [PRE_SALES_STATUS]: 3,
  [WON_STATUS]: 4,
  [ONBOARDING_STATUS]: 5,
  [POST_SALES_STATUS]: 6,
  [LOST_STATUS]: 7,
};

export const NUMBER_TO_STATUS: Record<number, string> = {
  0: "No status",
  1: DEAL_CREATED_STATUS,
  2: QUALIFIED_STATUS,
  3: PRE_SALES_STATUS,
  4: WON_STATUS,
  5: ONBOARDING_STATUS,
  6: POST_SALES_STATUS,
  7: LOST_STATUS,
};

// Doc status
export const DRAFT_STATUS = "Draft";
export const DRAFT_CREATED_STATUS = "Draft Created";
export const UNDER_REVIEW_STATUS = "Under Review";
export const APPROVED_STATUS = "Approved";
export const SUBMITTED_STATUS = "Submitted";

// Task status
export const IN_PROGRESS_STATUS = "In Progress";
export const DONE_STATUS = "Done";

export const STATUS_OPTIONS = [
  DEAL_CREATED_STATUS,
  QUALIFIED_STATUS,
  PRE_SALES_STATUS,
  WON_STATUS,
  LOST_STATUS,
  ONBOARDING_STATUS,
  POST_SALES_STATUS,
];

export const DOC_STATUS_OPTIONS = [
  DRAFT_CREATED_STATUS,
  UNDER_REVIEW_STATUS,
  APPROVED_STATUS,
  SUBMITTED_STATUS,
];

export const ORGANIZATION_LABEL = "Organization";
export const PRODUCTS_LABEL = "Products";
export const PEOPLE_LABEL = "People";
export const CONTENT_LABEL = "Content";
export const SECURITY_LABEL = "Security";
export const CLOUD_LABEL = "Cloud";
export const NETWORK_LABEL = "Network";
export const ACCESS_LABEL = "Access";
export const ASSETS_LABEL = "Assets";
export const INCIDENTS_LABEL = "Incidents";
export const DATA_LABEL = "Data";
export const DEVELOPMENT_LABEL = "Development";
export const CRYPTOGRAPHY_LABEL = "Cryptography";
export const VENDORS_LABEL = "Vendors";
export const DISASTER_LABEL = "Disaster";

// Priority Constants

// Nango Integrations
export const NANGO_GOOGLE_DRIVE_INTEGRATION = "google";
export const NANGO_CONFLUENCE_INTEGRATION = "confluence";
export const NANGO_NOTION_INTEGRATION = "notion";
export const NANGO_SALESFORCE_INTEGRATION = "salesforce";
export const NANGO_PIPEDRIVE_INTEGRATION = "pipedrive";
export const NANGO_SALESLOFT_INTEGRATION = "salesloft";
export const NANGO_GONG_INTEGRATION = "gong-oauth";
export const NANGO_HUBSPOT_INTEGRATION = "hubspot";
export const NANGO_GMAIL_INTEGRATION = "google-mail";
export const NANGO_GOOGLE_CALENDAR_INTEGRATION = "google-calendar";
export const NANGO_ONEDRIVE_INTEGRATION = "one-drive";
export const NANGO_SHAREPOINT_INTEGRATION = "sharepoint-online";
export const NANGO_MICROSOFT_TEAMS_INTEGRATION = "microsoft-teams";
export const NANGO_SLACK_INTEGRATION = "slack";

export const SNOWFLAKE_CRM_IMPORT_FLAG = "snowflake-crm-import";
// Gates Snowflake capabilities, including the product-taxonomy sync.
// Keep this aligned with the existing Snowflake CRM import rollout.
export const SNOWFLAKE_PRODUCT_TAXONOMY_FLAG = SNOWFLAKE_CRM_IMPORT_FLAG;
export const MCP_CONNECTORS_FEATURE_FLAG = "mcp-connectors";
export const NANGO_X_INTEGRATION = "twitter-oauth2-cc";
export const NANGO_CLARI_INTEGRATION = "clari-copilot";
export const NANGO_OUTREACH_INTEGRATION = "outreach";

// Trigger Task Display Keys
export const INSERT_DOCUMENT_TASK = "insert-document";
export const INVALIDATE_CACHE_TASK = "invalidate-cache";
export const DELETE_PERMISSION_TASK = "delete-permission";
export const SPREADSHEET_DRAFT_TASK = "spreadsheet-draft";
export const DOCUMENT_DRAFT_TASK = "document-draft";
export const DRAFT_SECTION_TASK = "draft-section";
export const DRAFT_QUESTION_TASK = "draft-question";
export const WRITE_DRAFT_TASK = "write-draft";
export const REFORMAT_DRAFT_TASK = "reformat-draft";
export const EXTRACT_REQUIREMENTS_TASK = "extract-requirements";
export const DRAFT_OUTLINE_TASK = "draft-outline";
export const DRAFT_SHEET_OUTLINE_TASK = "draft-sheet-outline";
export const ENRICH_CONTACT_TASK = "enrich-contact";
export const UPDATE_CLIENT_TASK = "update-client";
export const INSERT_DOCUMENT_PERMISSIONS_TASK = "insert-document-permissions";
export const DELETE_DOCUMENT_CHUNKS_TASK = "delete-document-chunks";
export const DELETE_FROM_ZILLIZ_TASK = "delete-from-zilliz";
export const DELETE_DOCUMENT_TASK = "delete-document";
export const UNDERSTAND_DOCUMENT_TASK = "understand-document";
export const REGENERATE_CONTEXT_TASK = "regenerate-context";
export const UPDATE_DEAL_TASK = "update-deal";
export const PARSE_JSON_TASK = "parse-json";
export const DOWNLOAD_OBJECT_TASK = "download-object";
export const DOWNLOAD_DRIVE_FILE_TASK = "download-drive-file";
export const DOWNLOAD_SLACK_FILE_TASK = "download-slack-file";
export const DOWNLOAD_CONFLUENCE_FILE_TASK = "download-confluence-file";
export const DOWNLOAD_NOTION_FILE_TASK = "download-notion-file";
export const PARSE_BUFFER_TASK = "parse-buffer";
export const CONVERT_DOCUMENT_TASK = "convert-document";
export const UPDATE_DOCUMENT_TASK = "update-document";
export const SEARCH_DOCUMENTS_TASK = "search-documents";
export const SEARCH_DEALS_TASK = "search-deals";
export const PLANNING_RESPONSE_TASK = "planning-response";
export const GATHERING_BACKGROUND_TASK = "gathering-background";
export const UPLOAD_FILE_TASK = "upload-file";
export const EMBED_DOCUMENT_TASK = "embed-document";
export const INSERT_DOCUMENT_CHUNKS_TASK = "insert-document-chunks";
export const INSERT_VESPA_TASK = "insert-vespa";
export const SELECT_DEAL_TASK = "select-deal";
export const SUMMARIZE_DOCUMENT_TASK = "summarize-document";
export const UPDATE_DOCUMENT_SUMMARY_TASK = "update-document-summary";
export const QUERY_VANTA_TASK = "query-vanta";
export const SYNC_HUBSPOT_ASSOCIATIONS_TASK = "sync-hubspot-associations";
export const GET_HUBSPOT_ASSOCIATIONS_TASK = "get-hubspot-associations";
export const GET_SALESFORCE_OPPORTUNITIES_TASK = "get-salesforce-opportunities";
export const SELECT_DOCUMENTS_TASK = "select-documents";
export const SEND_DRAFT_NOTIFICATION_TASK = "send-draft-notification";
export const SELECT_NOTIFICATION_SETTINGS_TASK = "select-notification-settings";
export const SEND_DRAFT_EMAIL_TASK = "send-draft-email";
export const SEND_DRAFT_SLACK_TASK = "send-draft-slack";
export const SELECT_SLACK_INSTALL_TASK = "select-slack-install";
export const RESPOND_SLACK_TASK = "respond-slack";
export const GENERATE_DOC_ID_TASK = "generate-doc-id";
export const INSERT_QUESTION_TASK = "insert-question";
export const DELETE_QUESTION_TASK = "delete-question";
export const INSERT_CLIENT_TASK = "insert-client";
export const DELETE_CLIENT_TASK = "delete-client";
export const INSERT_DEAL_TASK = "insert-deal";
export const DELETE_DEAL_TASK = "delete-deal";
export const INSERT_VERIFIED_ANSWER_TASK = "insert-verified-answer";
export const DELETE_VERIFIED_ANSWER_TASK = "delete-verified-answer";
export const ENRICH_ORG_TASK = "enrich-org";
export const REWRITE_QUESTION_TASK = "rewrite-question";
export const REWRITE_WITH_DEAL_CONTEXT = "rewrite-with-deal-context";
export const CUSTOMIZE_TEMPLATE_TASK = "customize-template";
export const DRAFT_EXECUTIVE_SUMMARY_TASK = "draft-executive-summary";
export const EXTRACT_VARIABLES_TASK = "extract-variables";
export const FILL_VARIABLE_TASK = "fill-variable";

// Deal Activity Types
export const DEAL_CREATED = "DEAL_CREATED";
export const STATUS_UPDATED = "STATUS_UPDATED";
export const DUE_DATE_UPDATED = "DUE_DATE_UPDATED";
export const COLLABORATOR_ADDED = "COLLABORATORS_ADDED";
export const COLLABORATOR_REMOVED = "COLLABORATORS_REMOVED";
export const COLLABORATOR_UPDATED = "COLLABORATORS_UPDATED";
export const DEAL_DOCUMENT_UPLOADED = "DEAL_DOCUMENT_UPLOADED";
export const DOCUMENT_DELETED = "DOCUMENT_DELETED";
export const SALESFORCE_SYNCED = "SALESFORCE_SYNCED";
export const HUBSPOT_SYNCED = "HUBSPOT_SYNCED";
export const RFP_RESPONSE_DRAFTED = "RFP_RESPONSE_DRAFTED";
export const SQ_RESPONSE_DRAFTED = "SQ_RESPONSE_DRAFTED";
export const ADDED_CONTACT = "ADDED_CONTACT";

// Trigger Tasks that we will show in the UI
export const SHOWN_TRIGGER_TASKS = [
  INSERT_DOCUMENT_PERMISSIONS_TASK,
  EMBED_DOCUMENT_TASK,
  SUMMARIZE_DOCUMENT_TASK,
  DOWNLOAD_DRIVE_FILE_TASK,
  DOWNLOAD_NOTION_FILE_TASK,
  UNDERSTAND_DOCUMENT_TASK,
  REGENERATE_CONTEXT_TASK,
  UPDATE_DEAL_TASK,
  DOCUMENT_DRAFT_TASK,
  SPREADSHEET_DRAFT_TASK,
  DRAFT_SECTION_TASK,
  QUERY_VANTA_TASK,
  EXTRACT_REQUIREMENTS_TASK,
  DRAFT_OUTLINE_TASK,
  ENRICH_CONTACT_TASK,
  UPDATE_CLIENT_TASK,
  DRAFT_SHEET_OUTLINE_TASK,
  REWRITE_QUESTION_TASK,
  SYNC_HUBSPOT_ASSOCIATIONS_TASK,
  GET_SALESFORCE_OPPORTUNITIES_TASK,
  CUSTOMIZE_TEMPLATE_TASK,
  DRAFT_EXECUTIVE_SUMMARY_TASK,
  SEARCH_DEALS_TASK,
  PLANNING_RESPONSE_TASK,
  GATHERING_BACKGROUND_TASK,
];

export const TRIGGER_TASK_DISPLAY_NAMES: Record<string, string> = {
  [INSERT_DOCUMENT_PERMISSIONS_TASK]: "Inserting document permissions",
  [EMBED_DOCUMENT_TASK]: "Embedding document",
  [SUMMARIZE_DOCUMENT_TASK]: "Summarizing content",
  [DOWNLOAD_DRIVE_FILE_TASK]: "Downloading from Drive",
  [DOWNLOAD_CONFLUENCE_FILE_TASK]: "Downloading from Confluence",
  [DOWNLOAD_NOTION_FILE_TASK]: "Downloading from Notion",
  [UNDERSTAND_DOCUMENT_TASK]: "Understanding document",
  [REGENERATE_CONTEXT_TASK]: "Regenerating context",
  [UPDATE_DEAL_TASK]: "Updating deal",
  [DOCUMENT_DRAFT_TASK]: "Drafting document",
  [DRAFT_SECTION_TASK]: "Drafting section",
  [DRAFT_QUESTION_TASK]: "Drafting question",
  [QUERY_VANTA_TASK]: "Downloading from Vanta",
  [EXTRACT_REQUIREMENTS_TASK]: "Extracting requirements",
  [DRAFT_OUTLINE_TASK]: "Drafting outline",
  [ENRICH_CONTACT_TASK]: "Enriching contact",
  [UPDATE_CLIENT_TASK]: "Updating client",
  [DRAFT_SHEET_OUTLINE_TASK]: "Drafting sheet outline",
  [REWRITE_QUESTION_TASK]: "Rewriting question with context",
  [SYNC_HUBSPOT_ASSOCIATIONS_TASK]: "Syncing Hubspot deal",
  [GET_SALESFORCE_OPPORTUNITIES_TASK]: "Syncing Salesforce opportunity",
  [CUSTOMIZE_TEMPLATE_TASK]: "Customizing template",
  [DRAFT_EXECUTIVE_SUMMARY_TASK]: "Drafting executive summary",
};

export const WORKFLOW_OPTIONS: Deal[] = [
  {
    name: "Request For Proposal",
    acronym: "RFP",
    description: "Draft a response to an RFP (Request For Proposal).",
  },
  {
    name: "Security Questionnaire",
    acronym: "SQ",
    description: "Draft a response to a security questionnaire.",
  },
  {
    name: "Customize a Template",
    acronym: "Customize",
    description: "Customize a template from your template library.",
  },
  {
    name: "Generate a Document",
    acronym: "Generate",
    description: "Have Paige generate a document for you.",
  },
  {
    name: "Blank Document",
    acronym: "BD",
    description: "Start from scratch.",
  },
  {
    name: "Draft an Email",
    acronym: "Email",
    description:
      "Reply to customer questions or share a document with a client.",
  },
];

export const TASK_WORKFLOW_OPTIONS: string[] = [
  "Draft an email",
  "Customize a template",
  "Draft a document",
  "Start a chat",
];

export const EDITABLE_DOC_TYPES: string[] = [
  "proposal",
  "questionnaire",
  "custom_document",
  "customized_template",
];

export const CUSTOM_DOC_TYPES: string[] = [
  "Proposal",
  "Onboarding",
  "Custom Document",
  "Customized Template",
];

export const VIEW_ONLY_DOC_TYPES: string[] = ["rfp", "sq", "info", "template"];

export const READABLE_DOC_TYPES: { [key: string]: string } = {
  proposal: "RFP Response",
  questionnaire: "SQ Response",
  custom_document: "Custom Document",
  custom_proposal: "Proposal",
  rfp: "RFP",
  sq: "Security Questionnaire",
  info: "Deal Context",
  template: "Template",
};

export const timePeriod: string[] = [
  "Today",
  "Last 7 days",
  "Last month",
  "Last 3 months",
  "Last 6 months",
  "All time",
];

export const timePeriodToPosthogFilter: Record<string, string> = {
  Today: "dStart",
  "Last 7 days": "-7d",
  "Last month": "-30d",
  "Last 3 months": "-90d",
  "Last 6 months": "-180d",
  "All time": "all",
};

export const PAGEVIEW_EVENT = "$pageview";
export const DOCUMENT_SIGNED_EVENT = "document_signed";
export const DOCUMENT_SENT_FOR_SIGNATURE_EVENT = "document_sent_for_signature";
export const DOCUMENT_CREATED_FOR_SHARING_EVENT =
  "document_created_for_sharing";
export const DOCUMENT_COPIED_TO_DEAL_EVENT = "document_copied_to_deal";
export const DOCUMENT_CREATED_EVENT = "document_created";
export const CHAT_STARTED_EVENT = "guest_chat_started";
export const TEMPLATE_USED_EVENT = "template_used";
export const DOCUMENT_PAGE_VIEWED_EVENT = "document_page_viewed";
export const CHAT_LOG_EVENT = "chat_log";
export const DRAFT_FEEDBACK_EVENT = "draft_feedback";
export const DEAL_RECOMMENDATION_EVENT = "deal_recommendation_used";
export const MEETING_LOG_EVENT = "meeting_log";
export const EMAIL_LOG_EVENT = "email_log";
export const DEAL_CREATED_EVENT = "deal_created";
export const DEAL_STATUS_CHANGED_EVENT = "deal_status_changed";
export const DEAL_CLOSED_EVENT = "deal_closed";
export const RELATED_DOCUMENT_CLICKED_EVENT = "related_document_clicked";
export const DOCUMENT_DOWNLOAD_EVENT = "document_downloaded";
export const QUESTION_ASKED_EVENT = "question_asked";

export const EVENT_TO_DESCRIPTION: Record<string, string> = {
  [DOCUMENT_CREATED_EVENT]: "created this document",
  [PAGEVIEW_EVENT]: "viewed this document",
  [DOCUMENT_SIGNED_EVENT]: "signed this document",
  [DOCUMENT_SENT_FOR_SIGNATURE_EVENT]: "sent this document for signature",
  [DOCUMENT_CREATED_FOR_SHARING_EVENT]: "created this document for sharing",
  [CHAT_STARTED_EVENT]: "started a chat",
};

export const DEFAULT_SCHEMA = {
  "template-schema": {
    type: "object",
    title: "Template data",
  },
  "template-bool": {
    type: "boolean",
    format: "checkbox",
  },
  "template-text": {
    type: "string",
    format: "textarea",
  },
  "template-object": {
    type: "object",
  },
  "template-loop": {
    type: "array",
    items: {
      type: "object",
    },
  },
  "template-row-loop": {
    $ref: "#/definitions/template-loop",
    format: "table",
  },
  "template-image": {
    type: "object",
    properties: {
      image_url: {
        $ref: "#/definitions/template-text",
        propertyOrder: 1,
        format: "url/file-download",
      },
      width: {
        type: "string",
        title: "width",
        propertyOrder: 2,
      },
      height: {
        type: "string",
        title: "height",
        propertyOrder: 3,
      },
    },
  },
  "template-markdown": {
    type: "object",
    properties: {
      markdown: {
        $ref: "#/definitions/template-text",
      },
    },
  },
  "template-html": {
    type: "object",
    properties: {
      html: {
        $ref: "#/definitions/template-text",
      },
    },
  },
  "template-content": {
    anyOf: [
      {
        title: "text",
        $ref: "#/definitions/template-text",
      },
      {
        title: "image",
        $ref: "#/definitions/template-image",
      },
      {
        title: "markdown",
        $ref: "#/definitions/template-markdown",
      },
      {
        title: "html",
        $ref: "#/definitions/template-html",
      },
    ],
  },
};

export const DOC_VIEWER_SCHEMA = {
  "template-schema": {
    type: "object",
    title: "Template data",
  },
  "template-bool": {
    type: "boolean",
    format: "checkbox",
  },
  "template-text": {
    type: "string",
    format: "textarea",
  },
  "template-object": {
    type: "object",
  },
  "template-loop": {
    type: "array",
    items: {
      type: "object",
    },
  },
  "template-row-loop": {
    $ref: "#/definitions/template-loop",
    format: "table",
  },
  "template-image": {
    type: "object",
    properties: {
      image_url: {
        $ref: "#/definitions/template-text",
        propertyOrder: 1,
        format: "url/file-download",
      },
      width: {
        type: "string",
        title: "width",
        propertyOrder: 2,
      },
      height: {
        type: "string",
        title: "height",
        propertyOrder: 3,
      },
    },
  },
  "template-markdown": {
    type: "object",
    properties: {
      markdown: {
        $ref: "#/definitions/template-text",
      },
    },
  },
  "template-html": {
    type: "object",
    properties: {
      html: {
        $ref: "#/definitions/template-text",
      },
    },
  },
  "template-content": {
    type: "object",
    properties: {
      description: {
        $ref: "#/definitions/template-text",
      },
    },
  },
};

export const QUOTE_TEMPLATE = "Quote";
export const PROPOSAL_TEMPLATE = "Proposal";
export const SOW_TEMPLATE = "Statement of Work";
export const MSA_TEMPLATE = "Master Services Agreement";
export const NDA_TEMPLATE = "Non-Disclosure Agreement";
export const PITCH_DECK_TEMPLATE = "Pitch Deck";
export const SLA_TEMPLATE = "Service-Level Agreement";
export const ONBOARDING_DOCUMENT = "Onboarding";
export const SUPPORT_ARTICLE = "Support Article";
export const ONE_PAGER = "One Pager";
export const CASE_STUDY = "Case Study";
export const WHITEPAPER = "Whitepaper";
export const BLOG_POST = "Blog Post";
export const EMAIL = "Email";
export const TICKET = "Ticket";
export const MEETING = "Meeting";
export const EBOOK = "Ebook";
export const WEBINAR = "Webinar";
export const VIDEO = "Video";
export const DEMO = "Demo";
export const TESTIMONIAL = "Testimonial";
export const OTHER_TEMPLATE = "Other";
export const BATTLE_CARD = "Battle Card";
export const SALES_PRESENTATION = "Sales Presentation";
export const SALES_PLAYBOOK = "Sales Playbook";
export const BROCHURE = "Brochure";
export const DATASHEET = "Datasheet";
export const NOTES = "Notes";
export const AD = "Ad";
export const FORUM_POST = "Forum Post";
export const SOCIAL_POST = "Social Post";
export const REVIEW = "Review";
export const HIRING = "Hiring";
export const SELLER_INTERVIEW = "Seller Interview";
export const BUYER_INTERVIEW = "Buyer Interview";
export const SELLER_SURVEY = "Seller Survey";
export const BUYER_SURVEY = "Buyer Survey";

// Event types
export const PRODUCT_UPDATE = "Product Update";
export const PRODUCT_LAUNCH = "Product Launch";
export const NEW_WEBSITE_PAGE = "New Website Page";
export const NEWS = "News";
export const WEBSITE_UPDATE = "Website Update";
export const BLOG_UPDATE = "Blog";
export const FUNDING_UPDATE = "Funding";
export const PARTNERSHIP_UPDATE = "Partnership";
export const ACQUISITION_UPDATE = "Acquisition";
export const CUSTOMER_UPDATE = "Customer";
export const TEAM_UPDATE = "Team Update";
export const MARKETING_CAMPAIGN = "Marketing Campaign";
export const OTHER_UPDATE = "Other";

export const COMPETITOR_INTEL_LABELS = [
  NEW_WEBSITE_PAGE,
  NEWS,
  WEBSITE_UPDATE,
  AD,
  FORUM_POST,
  SOCIAL_POST,
  REVIEW,
  BLOG_POST,
];

export const COMPETITOR_EVENT_TYPES = [
  PRODUCT_UPDATE,
  CUSTOMER_UPDATE,
  TEAM_UPDATE,
  MARKETING_CAMPAIGN,
  PRODUCT_LAUNCH,
  FUNDING_UPDATE,
  PARTNERSHIP_UPDATE,
  ACQUISITION_UPDATE,
];

export const COMPETITOR_UPDATE_TYPES_WITH_DESCRIPTIONS = {
  [NEW_WEBSITE_PAGE]: "A new web page was added",
  [NEWS]: "News",
  [WEBSITE_UPDATE]: "A web page was updated",
  [AD]: "An new advertisement was detected",
  [FORUM_POST]: "A new forum post was added",
  [SOCIAL_POST]: "A new social post was added",
  [REVIEW]: "A new review was detected",
};

export const EVENT_TYPES = [
  PRODUCT_UPDATE,
  PRODUCT_LAUNCH,
  FUNDING_UPDATE,
  PARTNERSHIP_UPDATE,
  ACQUISITION_UPDATE,
  CUSTOMER_UPDATE,
  TEAM_UPDATE,
  MARKETING_CAMPAIGN,
];

export const DEAL_LABEL_OPTIONS = [
  EMAIL,
  MEETING,
  NOTES,
  TICKET,
  SELLER_INTERVIEW,
  SELLER_SURVEY,
  BUYER_INTERVIEW,
  BUYER_SURVEY,
];

export const LABEL_OPTIONS = [
  EMAIL,
  MEETING,
  PROPOSAL_TEMPLATE,
  PITCH_DECK_TEMPLATE,
  ONE_PAGER,
  CASE_STUDY,
  WHITEPAPER,
  BLOG_POST,
  QUOTE_TEMPLATE,
  SOW_TEMPLATE,
  MSA_TEMPLATE,
  NDA_TEMPLATE,
  SLA_TEMPLATE,
  ONBOARDING_DOCUMENT,
  SUPPORT_ARTICLE,
  EBOOK,
  WEBINAR,
  VIDEO,
  DEMO,
  OTHER_TEMPLATE,
  CONTENT_LABEL,
  DATA_LABEL,
  DEVELOPMENT_LABEL,
  ORGANIZATION_LABEL,
  PEOPLE_LABEL,
  PRODUCTS_LABEL,
  SECURITY_LABEL,
  SALES_PLAYBOOK,
  BROCHURE,
  DATASHEET,
];

export const TEMPLATE_TYPES = [
  PROPOSAL_TEMPLATE,
  PITCH_DECK_TEMPLATE,
  ONE_PAGER,
  CASE_STUDY,
  WHITEPAPER,
  BLOG_POST,
  QUOTE_TEMPLATE,
  SOW_TEMPLATE,
  MSA_TEMPLATE,
  NDA_TEMPLATE,
  SLA_TEMPLATE,
  ONBOARDING_DOCUMENT,
  SUPPORT_ARTICLE,
  EBOOK,
  WEBINAR,
  VIDEO,
  DEMO,
  OTHER_TEMPLATE,
];

export const BILLING_FREQUENCY = [
  "One Time",
  "Monthly",
  "Quarterly",
  "Semi-Annually",
  "Yearly",
  "Every 2 Years",
  "Every 3 Years",
  "Every 4 Years",
  "Every 5 Years",
];

export const CONTENT_LENGHTS = [
  "One word",
  "A few words",
  "A sentence",
  "A paragraph",
  "Multiple paragraphs",
];

export const REPORT_QUESTIONS = [
  {
    section: "General",
    questions: [
      {
        question:
          "What are the primary pain points the customer is experiencing?",
      },
      {
        question:
          "What are the main objectives the customer is hoping to achieve with our solution?",
      },
    ],
  },
  {
    section: "Solution Details",
    questions: [
      {
        question: "What solution are we proposing to the customer?",
      },
      {
        question:
          "What key features of our solution address the customer's pain points?",
      },
      {
        question:
          "What is the value proposition of our solution for this customer?",
      },
      {
        question: "What pricing model and terms have been proposed?",
      },
      {
        question:
          "What is the estimated return on investment for the customer?",
      },
    ],
  },
  {
    section: "Challenges / Risks",
    questions: [
      {
        question:
          "What challenges or objections have been raised by the customer?",
      },
      {
        question: "What technical risks or product gaps exist?",
      },
      {
        question: "Who are the main competitors in this deal?",
      },
    ],
  },
  {
    section: "Sales Process",
    questions: [
      {
        question: "What are the next steps in the sales process?",
      },
      {
        question: "What internal resources or teams are involved in this deal?",
      },
    ],
  },
  {
    section: "Implementation Plan",
    questions: [
      {
        question: "What is the proposed timeline for implementation?",
      },
      {
        question: "How ready is the customer for implementation?",
      },
    ],
  },
];

export const DEFAULT_HUBSPOT_DEAL_FIELDS = [
  { id: "dealname", name: "Deal Name", segment: false, disabled: true },
  { id: "dealstage", name: "Deal Stage", segment: false, disabled: true },
  { id: "amount", name: "Amount", segment: false, disabled: true },
  { id: "closedate", name: "Close Date", segment: false, disabled: true },
  {
    id: "hs_lastmodifieddate",
    name: "Last Modified Date",
    segment: false,
    disabled: true,
  },
  {
    id: "hs_closed_amount",
    name: "Closed Amount",
    segment: false,
    disabled: true,
  },
  {
    id: "hs_closed_amount_currency_code",
    name: "Closed Amount Currency",
    segment: false,
    disabled: true,
  },
  {
    id: "hs_forecast_amount",
    name: "Forecast Amount",
    segment: false,
    disabled: true,
  },
  {
    id: "hs_forecast_amount_currency_code",
    name: "Forecast Amount Currency",
    segment: false,
    disabled: true,
  },
  { id: "hs_pipeline", name: "Pipeline", segment: false, disabled: true },
  {
    id: "hs_pipeline_stage",
    name: "Pipeline Stage",
    segment: false,
    disabled: true,
  },
  { id: "dealtype", name: "Deal Type", segment: false, disabled: true },
  { id: "description", name: "Description", segment: false, disabled: true },
];

export const DEFAULT_HUBSPOT_ASSOCIATIONS = [
  { id: "companies", name: "Companies", segment: false, disabled: true },
  { id: "contacts", name: "Contacts", segment: false, disabled: true },
  { id: "line_items", name: "Line Items", segment: false, disabled: true },
  { id: "meetings", name: "Meetings", segment: false, disabled: true },
  { id: "notes", name: "Notes", segment: false, disabled: true },
  { id: "emails", name: "Emails", segment: false, disabled: true },
  { id: "tickets", name: "Tickets", segment: false, disabled: true },
];

export const DEFAULT_HUBSPOT_ACOUNT_FIELDS = [
  { id: "name", name: "Name", segment: false, disabled: true },
  { id: "industry", name: "Industry", segment: false, disabled: true },
  { id: "city", name: "City", segment: false, disabled: true },
  { id: "state", name: "State", segment: false, disabled: true },
  { id: "country", name: "Country", segment: false, disabled: true },
  { id: "domain", name: "Domain", segment: false, disabled: true },
  { id: "description", name: "Description", segment: false, disabled: true },
  { id: "about_us", name: "About Us", segment: false, disabled: true },
  { id: "founded_year", name: "Founded Year", segment: false, disabled: true },
  {
    id: "annualrevenue",
    name: "Annual Revenue",
    segment: false,
    disabled: true,
  },
  { id: "website", name: "Website", segment: false, disabled: true },
  {
    id: "linkedin_company_page",
    name: "LinkedIn Company Page",
    segment: false,
    disabled: true,
  },
  {
    id: "numberofemployees",
    name: "Number of Employees",
    segment: false,
    disabled: true,
  },
  { id: "hs_lead_status", name: "Lead Status", segment: false, disabled: true },
  { id: "hs_createdate", name: "Create Date", segment: false, disabled: true },
  {
    id: "hs_lastmodifieddate",
    name: "Last Modified Date",
    segment: false,
    disabled: true,
  },
  {
    id: "total_money_raised",
    name: "Total Money Raised",
    segment: false,
    disabled: true,
  },
];

export const DEFAULT_SALESFORCE_OPPORTUNITY_FIELDS = [
  { id: "Id", name: "Id", segment: false, disabled: true },
  { id: "Name", name: "Name", segment: false, disabled: true },
  { id: "Description", name: "Description", segment: false, disabled: true },
  { id: "Amount", name: "Amount", segment: false, disabled: true },
  { id: "StageName", name: "Stage Name", segment: false, disabled: true },
  { id: "CloseDate", name: "Close Date", segment: false, disabled: true },
];

export const DEFAULT_SALESFORCE_ASSOCIATION_FIELDS = [
  { id: "Account", name: "Account", segment: false, disabled: true },
  {
    id: "OpportunityContactRole",
    name: "Contact",
    segment: false,
    disabled: true,
  },
  {
    id: "OpportunityLineItem",
    name: "Line Item",
    segment: false,
    disabled: true,
  },
  { id: "EmailMessage", name: "Email", segment: false, disabled: true },
  { id: "Note", name: "Note", segment: false, disabled: true },
  {
    id: "ContentDocumentLink",
    name: "Document",
    segment: false,
    disabled: true,
  },
  { id: "FeedItem", name: "Feed Item", segment: false, disabled: true },
  { id: "Event", name: "Event", segment: false, disabled: true },
];

export const DEFAULT_SALESFORCE_ACCOUNT_FIELDS = [
  { id: "Id", name: "Id", segment: false, disabled: true },
  { id: "Name", name: "Name", segment: false, disabled: true },
  { id: "Industry", name: "Industry", segment: false, disabled: true },
  {
    id: "AnnualRevenue",
    name: "Annual Revenue",
    segment: false,
    disabled: true,
  },
  {
    id: "BillingAddress",
    name: "Billing Address",
    segment: false,
    disabled: true,
  },
  { id: "BillingCity", name: "Billing City", segment: false, disabled: true },
  {
    id: "BillingCountry",
    name: "Billing Country",
    segment: false,
    disabled: true,
  },
  { id: "BillingState", name: "Billing State", segment: false, disabled: true },
  { id: "Description", name: "Description", segment: false, disabled: true },
  {
    id: "NumberOfEmployees",
    name: "Number of Employees",
    segment: false,
    disabled: true,
  },
  { id: "Phone", name: "Phone", segment: false, disabled: true },
  { id: "Website", name: "Website", segment: false, disabled: true },
];

export const SURVEY_LINK = (token: string) =>
  `${process.env.NEXT_PUBLIC_SITE_URL}/survey?token=${token}`;

export const UNSUBSCRIBE_LINK = (token: string) =>
  `${process.env.NEXT_PUBLIC_SITE_URL}/unsubscribe?token=${token}`;

export const TEST_SURVEY_LINK = (dealId: string | null | undefined, userId: string) =>
  `${process.env.NEXT_PUBLIC_SITE_URL}/survey/test?${new URLSearchParams({
    ...(dealId ? { dealId } : {}),
    respondentId: userId,
  })}`;

export const SIMPLIFIED_DB_SCHEMA = `
deals (
  id text ,
  name text ,
  org_id text  ,
  client_id text ,
  proposal_due timestamp with time zone ,
  collaborators text[] ,
  amount bigint ,
  status_id text ,
  hubspot_custom_properties jsonb ,
  salesforce_custom_properties jsonb ,
  region text ,
  type text ,
  constraint deals_client_id_fkey foreign key (client_id) references clients (id) on delete set 
);
clients (
  id text ,
  name text ,
  org_id text  default requesting_org_id(),
  industry text ,
  num_employees bigint ,
  city text ,
  state text ,
  country text ,
  short_description text ,
  annual_revenue bigint ,
  hubspot_custom_properties jsonb ,
  salesforce_custom_properties jsonb ,
);
contacts (
  id text ,
  client_id text ,
  departments text[] ,
  functions text[] ,
  seniority text ,
  org_id text ,
  title text ,
  constraint contacts_client_id_fkey foreign key (client_id) references clients (id),
);
deal_contact_associations (
  deal_id text ,
  contact_id text ,
  org_id text  ,
  constraint deal_contact_associations_contact_id_fkey foreign key (contact_id) references contacts (id) ,
  constraint deal_contact_associations_deal_id_fkey foreign key (deal_id) references deals (id) 
);
drivers (
  reason_id uuid ,
  deal_id text ,
  positive boolean ,
  summary text ,
  org_id text  ,
  score bigint ,
  constraint drivers_deal_id_fkey foreign key (deal_id) references deals (id) ,
  constraint drivers_reason_id_fkey foreign key (reason_id) references reasons (id) 
); 
reasons (
  id uuid  ,
  name text ,
  description text ,
  org_id text  ,
  tag text ,
);
reason_instances (
  reason_id uuid ,
  deal_id text ,
  document_id text ,
  org_id text  ,
  quote text ,
  positive boolean ,
  is_crm_property boolean ,
  id uuid  ,
  external boolean ,
  constraint reason_instances_deal_id_fkey foreign key (deal_id) references deals (id) ,
  constraint reason_instances_document_id_fkey foreign key (document_id) references documents (id) ,
  constraint reason_instances_reason_id_fkey foreign key (reason_id) references reasons (id) ,
  constraint reason_instances_competitor_id_fkey foreign KEY (competitor_id) references competitors (id),
);
line_items (
  deal_id text ,
  product_id text ,
  quantity double precision ,
  org_id text  ,
  constraint line_items_deal_id_fkey foreign key (deal_id) references deals (id) ,
  constraint line_items_product_id_fkey foreign key (product_id) references products (id) 
);
products (
  id text ,
  name text ,
  org_id text ,
  price real ,
  billing_frequency text ,
  description text 
);

competitors (
  id text,
  name text,
);

deal_competitor_associations (
  deal_id text,
  competitor_id,
  constraint deal_competitor_associations_competitor_id_fkey foreign KEY (competitor_id) references competitors (id),
  constraint deal_competitor_associations_deal_id_fkey foreign KEY (deal_id) references deals (id)
) TABLESPACE pg_default;

Table descriptions & notes:
Deals: Contains information about the deals.
proposal_due is equivalent to close date.

Clients: Contains information about the clients (same as accounts)

Deal_contact_associations: Use this to join deals and contacts if the user wants to filter based on contacts.

Drivers: Each row is a specific decision driver instance for why a deal was won or lost. Positive is true if the driver was a positive factor in the deal.
When a user wants to filter based on a specific decision driver, this should be joined with reasons.

Reasons: The descriptions of names of drivers. The tags should be used to filter as follows:
"Competitor": User wants to filter by competitor
"Product": User wants to filter by product related drivers (feature requests, integrations)
"Price": User wants to filter by price related drivers (discounts, pricing)
"Brand": User wants to filter by brand related drivers (reputation, trust)

Reason_instances: Each row is a specific instance of a reason being mentioned in a deal with a quote. This is useful for filtering based on specific quotes from documents.
External is true if the quote came from a buyer/customer/prospect.

Line_items: Use this to join deals and products when the user wants to filter based on a specific product.

Products: Contains information about the products.

Competitors: Contains information about the competitors.

Deal_competitor_associations: Use this to join deals and competitors when the user wants to filter based on a specific competitor.

Always select human readable columns like names and descriptions.
`;

export const DEFAULT_EMAIL_BODY = `
Hi {name},

We heard you went with a different solution - completely understood.

We're offering {incentive_amount} Amazon gift cards (or donation to a charity of your choice) to buyers who take a 2-minute [survey]({link}) to share feedback.

Your insight helps us get better, and we'd love to hear from you.

Thanks,
`;

export const DEFAULT_EMAIL_BODY_EXTERNAL = `
Hi {name},

Hope you're doing well! The team mentioned you went in a different direction - totally understand and I appreciate you giving us a look.

They recommended I reach out to gather some honest feedback - we're always trying to get better and evals like yours are the most useful input we get.

Our leadership team is offering {incentive_amount} Amazon gift cards for those that help out with a 10-minute call to share what drove the decision.

You can use this link to [schedule a short interview]({booking_link}).

If you'd prefer something shorter, async you can also use our [AI interviewer]({link}) to share your thoughts in 2-3 minutes whenever convenient.

Either way, thanks for your time during the process.

`;

export const DEFAULT_EMAIL_BODY_WIN_AI = `
Hi {name},

The team is super excited to work with you.
Now that things are up and running, we'd love to hear what made us the right choice.

It only takes 2-3 minutes - you can share your thoughts with our [AI interviewer]({link}) whenever it's convenient.
We're throwing in an {incentive_amount} gift card as thanks.

Thanks for choosing us,

`;

export const DEFAULT_EMAIL_BODY_CHURN_EXTERNAL = `
Hi {name},

Hope you're doing well. I noticed your subscription wrapped up and wanted to reach out directly.

We'd love to understand what drove the decision - your honest feedback helps the whole team improve.

If you have 10 minutes, you can [schedule a short call]({booking_link}), or if you'd prefer something async, our [AI interviewer]({link}) only takes 2-3 minutes whenever it works for you.
We're including a {incentive_amount} gift card as a thank you for your time and insights.

Either way, really appreciate the time you gave us.
`;

export const DEFAULT_EMAIL_BODY_SELLER = `
Hi {name},

Hope all is well! We recently wrapped up the {deal_name} deal and wanted to get your perspective on how it went.

Your front-line insight is invaluable for the whole team - we'd love to hear what worked, what didn't, and anything that could help us improve.

When you get a chance, it only takes a few minutes to share your thoughts with our [AI interviewer]({link}).

Thanks so much,
`;

export const DEFAULT_EMAIL_BODY_FOLLOWUP_BUYER_LOSS = `
Hi {name},

Just following up on my previous email — wanted to make sure this didn't get buried.

If you have 2-3 minutes, your honest feedback would mean a lot to the team.

[Share your thoughts here]({link})

We're still offering the {incentive_amount} gift card as a thank you.

Appreciate your time either way.
`;

export const DEFAULT_EMAIL_BODY_FOLLOWUP_SELLER = `
Hi {name},

Just checking in — wanted to make sure you saw my earlier note about {deal_name}.

Your frontline perspective really helps the whole team improve. It only takes a couple of minutes.

[Click here to share your thoughts]({link})

Thanks so much,
`;

export const DEFAULT_EMAIL_BODY_FOLLOWUP_WIN = `
Hi {name},

Just following up on my last email — we'd still love to hear what made us the right choice!

[Share your thoughts here]({link}) — it only takes 2-3 minutes.

We're still including the {incentive_amount} gift card as a thank you for your time.

Thanks again for choosing us,
`;

export const WHY_WE_WIN_CARD = "why_we_win";
export const WHY_WE_LOSE_CARD = "why_we_lose";
export const LAND_MINE_QUESTIONS_CARD = "land_mine_questions";
export const CUSTOMERS_CARD = "customers";
export const PRICING_COMPARISON_CARD = "pricing_comparison";
export const FEATURE_COMPARISON_CARD = "feature_comparison";
export const COMPETITOR_OVERVIEW_CARD = "competitor_overview";

export const COMPETITOR_CARD_TITLES: Record<string, string> = {
  [WHY_WE_WIN_CARD]: "Why We Win",
  [WHY_WE_LOSE_CARD]: "Objection Handling",
  [LAND_MINE_QUESTIONS_CARD]: "Land Mine Questions",
  [CUSTOMERS_CARD]: "Customers Who Chose Us",
  [PRICING_COMPARISON_CARD]: "Pricing Comparison",
  [FEATURE_COMPARISON_CARD]: "Feature Comparison",
  [COMPETITOR_OVERVIEW_CARD]: "Competitor Overview",
};

export const CARD_ORDERS: Record<string, number> = {
  [WHY_WE_WIN_CARD]: 0,
  [WHY_WE_LOSE_CARD]: 1,
  [LAND_MINE_QUESTIONS_CARD]: 2,
  [CUSTOMERS_CARD]: 3,
  [PRICING_COMPARISON_CARD]: 4,
  [FEATURE_COMPARISON_CARD]: 5,
  [COMPETITOR_OVERVIEW_CARD]: 6,
};

export const DEFAULT_SENDING_EMAIL = "noreply@usehindsight.com";

// Default email variants seeded when the Interview Agent is first enabled for an org.
// These are inserted if the org has no variants at the time they enable the agent.
export const DEFAULT_INTERVIEW_AGENT_VARIANTS: Array<{
  name: string;
  subject_line: string;
  body: string;
  survey_length: number;
  survey_reminder_days: number;
  contact_link: string;
  sending_email: string;
  external: boolean;
}> = [
  // ── Follow-up templates (reply-in-thread step 2 for each sequence type) ──────
  {
    name: "Default: Loss Interview Follow-up (Buyer)",
    subject_line: "Re: We'd appreciate some feedback",
    body: DEFAULT_EMAIL_BODY_FOLLOWUP_BUYER_LOSS,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Seller Interview Follow-up",
    subject_line: "Re: Quick question about {deal_name}",
    body: DEFAULT_EMAIL_BODY_FOLLOWUP_SELLER,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: false,
  },
  {
    name: "Default: Win Interview Follow-up (Buyer)",
    subject_line: "Re: A gift card for your thoughts?",
    body: DEFAULT_EMAIL_BODY_FOLLOWUP_WIN,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Loss Interview (Live + AI)",
    subject_line: "Donation for your feedback",
    body: DEFAULT_EMAIL_BODY_EXTERNAL,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Loss Interview (AI only)",
    subject_line: "We'd appreciate some feedback (paid)",
    body: DEFAULT_EMAIL_BODY,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Win Interview (AI only)",
    subject_line: "A gift card for your thoughts?",
    body: DEFAULT_EMAIL_BODY_WIN_AI,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Churn Interview (Live + AI)",
    subject_line: "Here to learn - looking for feedback",
    body: DEFAULT_EMAIL_BODY_CHURN_EXTERNAL,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: true,
  },
  {
    name: "Default: Seller Interview",
    subject_line: "Quick question about {deal_name}",
    body: DEFAULT_EMAIL_BODY_SELLER,
    survey_length: 5,
    survey_reminder_days: 3,
    contact_link: "",
    sending_email: DEFAULT_SENDING_EMAIL,
    external: false,
  },
];

// Add custom theme using Tailwind's sky colors
export const customTheme: Partial<Theme> = {
  accentColor: "rgb(14, 165, 233)", // sky-500
  accentLight: "rgba(14, 165, 233, 0.05)", // sky-500 with opacity
  accentFg: "white",
  borderColor: "rgb(226, 232, 240)", // slate-200 for subtle borders
  bgHeader: "rgba(241, 245, 249)",
  bgHeaderHovered: "rgba(241, 245, 249)",
  bgHeaderHasFocus: "rgba(241, 245, 249)",
  bgCell: "white",
  fontFamily: "Geist",
  bgCellMedium: "rgba(241, 245, 249, 0.5)",
};

// Decision Driver Tags/Categories
export const DECISION_DRIVER_TAGS = [
  "Sales Execution", 
  "Relationship",
  "Pricing",
  "Product",
  "Implementation",
  "Buyer Behavior",
  "Messaging",
  "Messaging Accuracy",
  "Competitor",
] as const;

export type DecisionDriverTag = typeof DECISION_DRIVER_TAGS[number];

// Default Custom Metrics that match our schema
export const DEFAULT_CUSTOM_METRICS: DefaultMetric[] = [
  {
    id: "product-fit",
    name: "Product Fit",
    description: "How well our product matched the customer's technical and functional requirements",
    guidelines: {
      "1": "Poor fit - Product lacks key required features or capabilities",
      "2": "Below average fit - Product meets some requirements but has gaps",
      "3": "Average fit - Product meets most requirements with minor gaps",
      "4": "Good fit - Product meets nearly all requirements well",
      "5": "Perfect fit - Product exceeds requirements and provides additional value"
    },
    tag: "Product" as DecisionDriverTag,
    rules: {} as any, // Empty rules means applies to all deals
    driver_filter: {
      tags: ["Product"]
    },
    score_key: "wl_product_fit_score",
    explanation_key: "wl_product_fit_explanation",
  },
  {
    id: "sales-execution",
    name: "Sales Execution", 
    description: "How effectively our sales team executed throughout the sales process",
    guidelines: {
      "1": "Poor execution - Missed key opportunities, poor communication, unresponsive",
      "2": "Below average execution - Some missed opportunities, inconsistent follow-up",
      "3": "Average execution - Met basic expectations, standard process",
      "4": "Good execution - Proactive communication, well-prepared, good process",
      "5": "Excellent execution - Exceptional process, perfect timing, outstanding preparation"
    },
    tag: "Sales Execution" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {
      tags: ["Sales Execution"]
    },
    score_key: "wl_sales_execution_score",
    explanation_key: "wl_sales_execution_explanation",
  },
  {
    id: "relationship-strength",
    name: "Relationship Strength",
    description: "Quality and strength of relationships built with key stakeholders",
    guidelines: {
      "1": "Weak relationship - Little trust, poor rapport, resistance",
      "2": "Below average relationship - Some trust issues, limited rapport",
      "3": "Average relationship - Professional but not deep, neutral rapport",
      "4": "Good relationship - Strong trust, good rapport, advocates present",
      "5": "Excellent relationship - Deep trust, strong champions, enthusiastic support"
    },
    tag: "Relationship" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {
      tags: ["Relationship"]
    },
    score_key: "wl_relationship_score",
    explanation_key: "wl_relationship_explanation",
  },
  {
    id: "pricing-fit",
    name: "Pricing Fit",
    description: "How well our pricing aligned with the customer's budget and value perception",
    guidelines: {
      "1": "Poor fit - Significantly over budget, major pricing objections",
      "2": "Below average fit - Somewhat over budget, multiple pricing concerns",
      "3": "Average fit - Within budget range, some pricing discussions",
      "4": "Good fit - Competitive pricing, minor budget concerns",
      "5": "Perfect fit - Excellent value perception, pricing advantage"
    },
    tag: "Pricing" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {
      tags: ["Pricing"]
    },
    score_key: "wl_price_sensitivity_score",
    explanation_key: "wl_price_sensitivity_explanation",
  },
  {
    id: "competitive-performance",
    name: "Competitive Performance",
    description: "How well we performed against competitors in this deal",
    guidelines: {
      "1": "Weak position - Customer clearly preferred competitors, we were not seriously considered",
      "2": "Disadvantaged - Competitors had clear advantages, we struggled to differentiate",
      "3": "Competitive - Evenly matched with competitors, close evaluation",
      "4": "Strong position - We had clear advantages over most competitors",
      "5": "Dominant - We were the obvious choice, competitors couldn't match our value"
    },
    tag: "Competitor" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {}, // No filter - applies to all deals
    score_key: "wl_competitive_intensity_score",
    explanation_key: "wl_competitive_intensity_explanation",
  },
  {
    id: "customer-fit",
    name: "Customer Fit",
    description: "How well our solution aligned with the customer's unique needs and context",
    guidelines: {
      "1": "Poor fit - Customer profile is outside our target market; fundamental misalignment in scale or industry.",
      "2": "Below average fit - Significant hurdles in alignment; customer needs require heavy customization or workaround.",
      "3": "Average fit - Standard use case; customer aligns with our Ideal Customer Profile (ICP) but lacks high strategic synergy.",
      "4": "Good fit - Strong alignment with customer’s business model and industry; clear path to long-term success.",
      "5": "Perfect fit - Ideal customer profile; our solution is specifically designed for their exact scale, culture, and industry."
    },
    tag: "Customer" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {}, // No filter - applies to all deals
    score_key: "wl_customer_fit_score",
    explanation_key: "wl_customer_fit_explanation",
  },
  {
    id: "messsaging-fit",
    name: "Messaging Fit",
    description: "How well our messaging resonated with the customer's priorities and pain points",
    guidelines: {
      "1": "Poor resonance - Messaging fell flat; customer felt we didn't understand their industry or specific pain points.",
      "2": "Below average resonance - Customer was skeptical; messaging felt generic or disconnected from their primary goals.",
      "3": "Average resonance - Messaging was clear and understood, but didn't create significant excitement or urgency.",
      "4": "Good resonance - Messaging hit on key priorities; customer used our own language to describe their needs.",
      "5": "Perfect resonance - Messaging was a primary driver of the deal; customer felt we had a unique 'insider' understanding of their problems."
    },
    tag: "Messaging" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {}, // No filter - applies to all deals
    score_key: "wl_messaging_fit_score",
    explanation_key: "wl_messaging_fit_explanation",
  },
  {
    id: "messaging-accuracy",
    name: "Messaging Accuracy",
    description: "How accurately our messaging reflected the customer's needs and our solution's value",
    guidelines: {
      "1": "Highly inaccurate - Major disconnect between sales promises and product reality; set unrealistic expectations.",
      "2": "Somewhat inaccurate - Messaging over-indexed on roadmap features or exaggerated current capabilities.",
      "3": "Accurate - Messaging was honest and factual, though perhaps lacked depth in technical or specific nuances.",
      "4": "Very accurate - High degree of precision; messaging correctly identified where we excel and where we don't.",
      "5": "Perfectly accurate - Flawless representation of value; customer's post-sale experience perfectly mirrors the pre-sale narrative."
    },
    tag: "Messaging Accuracy" as DecisionDriverTag,
    rules: {} as any,
    driver_filter: {}, // No filter - applies to all deals
    score_key: "wl_messaging_accuracy_score",
    explanation_key: "wl_messaging_accuracy_explanation",
  },
];


// Color palette for default metrics (matching scorecard column)
export const defaultMetricColors: Record<string, { colorFrom: string; colorTo: string }> = {
  "wl_product_fit_score": { colorFrom: "#60a5fa", colorTo: "#3b82f6" }, // blue
  "wl_sales_execution_score": { colorFrom: "#34d399", colorTo: "#10b981" }, // emerald
  "wl_relationship_score": { colorFrom: "#c084fc", colorTo: "#a855f7" }, // purple
  "wl_price_sensitivity_score": { colorFrom: "#fbbf24", colorTo: "#f59e0b" }, // amber
  "wl_competitive_intensity_score": { colorFrom: "#f87171", colorTo: "#ef4444" }, // red
  "wl_customer_fit_score": { colorFrom: "#60faaa", colorTo: "#3bf695" }, // teal
  "wl_messaging_fit_score": { colorFrom: "#fa60e3", colorTo: "#f63bea" }, // sky
  "wl_messaging_accuracy_score": { colorFrom: "#252b6e", colorTo: "#201a70" }, // emerald
};

// Color palette for custom metrics (cycles through these)
export const customMetricColorPalette = [
  { colorFrom: "#38bdf8", colorTo: "#0ea5e9" }, // sky
  { colorFrom: "#fb923c", colorTo: "#f97316" }, // orange
  { colorFrom: "#a3e635", colorTo: "#84cc16" }, // lime
  { colorFrom: "#f472b6", colorTo: "#ec4899" }, // pink
  { colorFrom: "#22d3ee", colorTo: "#06b6d4" }, // cyan
  { colorFrom: "#818cf8", colorTo: "#6366f1" }, // indigo
  { colorFrom: "#facc15", colorTo: "#eab308" }, // yellow
  { colorFrom: "#4ade80", colorTo: "#22c55e" }, // green
];

export const DEFAULT_DECISION_DRIVERS = [
  // Sales Execution
  {
    id: "discovery-quality",
    name: "Discovery Quality",
    description: "How well the seller understood needs, asked the right questions",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "qualification-quality",
    name: "Qualification Quality", 
    description: "Whether reps are bringing in the right deals and contacts (BANT, MEDDIC, etc.)",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "responsiveness",
    name: "Responsiveness",
    description: "Speed and quality of responses during the process",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "demo-effectiveness",
    name: "Demo Effectiveness",
    description: "How well the demo showcased relevant value, resonated with buyer needs or highlighted key differentiators",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "follow-up-communication",
    name: "Follow-Up / Communication",
    description: "Find references to whether the seller maintained regular, clear communication throughout the process",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "rep-knowledge-expertise", 
    name: "Rep Knowledge / Expertise",
    description: "Seller credibility, product expertise, and domain / industry knowledge",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "poc-pilot-experience",
    name: "POC / Pilot Experience", 
    description: "The success or limitations of a pilot, proof of concept, or trial",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "deal-progression-stalling",
    name: "Deal Progression / Stalling",
    description: "Whether the deal moved at expected pace or got stuck",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "playbook-adherence",
    name: "Playbook Adherence", 
    description: "Whether reps followed defined sales stages/process",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "objection-handling",
    name: "Objection Handling",
    description: "How well reps addressed buyer concerns or pushback",
    tag: "Sales Execution" as DecisionDriverTag,
  },
  {
    id: "value-proposition-clarity",
    name: "Value Proposition Clarity",
    description: "Whether buyer walked away understanding the value",
    tag: "Sales Execution" as DecisionDriverTag,
  },

  // Relationship
  {
    id: "decision-maker-alignment",
    name: "Decision Maker Alignment",
    description: "Engagement across key personas, especially decision makers", 
    tag: "Relationship" as DecisionDriverTag,
  },
  {
    id: "team-fit-relationship",
    name: "Team Fit / Relationship",
    description: "Chemistry and rapport with the seller's team",
    tag: "Relationship" as DecisionDriverTag,
  },
  {
    id: "internal-champion-influence",
    name: "Internal Champion Influence",
    description: "Influence and credibility of the champion internally",
    tag: "Relationship" as DecisionDriverTag,
  },
  {
    id: "executive-buy-in",
    name: "Executive Buy-In",
    description: "Whether executive stakeholders were bought in",
    tag: "Relationship" as DecisionDriverTag,
  },
  {
    id: "multi-threading",
    name: "Multi-threading", 
    description: "Whether reps engaged multiple stakeholders early",
    tag: "Relationship" as DecisionDriverTag,
  },

  // Pricing & Packaging
  {
    id: "pricing-clarity",
    name: "Pricing Clarity",
    description: "Whether pricing was clearly communicated",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "value-perception",
    name: "Value Perception",
    description: "Whether buyer felt they got value for the price",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "discounting-negotiation",
    name: "Discounting / Negotiation",
    description: "How price discussions and concessions were handled",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "contract-flexibility",
    name: "Contract Flexibility",
    description: "Willingness to adjust commercial terms to meet buyer needs",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "budget-changes-constraints",
    name: "Budget Changes / Constraints",
    description: "Budget issues, cutbacks, or reprioritization on the buyer side",
    tag: "Pricing" as DecisionDriverTag,
  },

  // Product & Features  
  {
    id: "product-gaps",
    name: "Product Gaps",
    description: "Missing or weak features",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "integrations-apis",
    name: "Integrations / APIs",
    description: "Needs for specific integrations",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "ux-ui-quality",
    name: "UX / UI Quality", 
    description: "Design, usability, and ease of use",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "technical-fit",
    name: "Technical Fit",
    description: "How well product works in buyer's stack",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "security-compliance",
    name: "Security / Compliance",
    description: "Buyer needs around SOC 2, HIPAA, GDPR, etc.",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "performance-reliability",
    name: "Performance & Reliability",
    description: "Downtime, speed, bugs impacting decision", 
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "feature-parity",
    name: "Feature Parity",
    description: "Whether we matched capabilities of an incumbent or competitor",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "customization-flexibility",
    name: "Customization / Flexibility",
    description: "Ability to adapt workflows, fields, or UI to buyer needs",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "roadmap-alignment",
    name: "Roadmap Alignment",
    description: "Whether product direction matched buyer's strategic goals",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "admin-experience",
    name: "Admin Experience",
    description: "Usability for admins managing users, permissions, configurations",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "enterprise-readiness",
    name: "Enterprise Readiness",
    description: "Access controls, audit logs, SSO, data residency, SLAs, and other topics related to being suited for enterprise customers",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "ai-automation-capabilities",
    name: "AI / Automation Capabilities", 
    description: "Whether automation or AI functionality met buyer expectations",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "in-house-diy-solutions",
    name: "In-house / DIY Solutions",
    description: "Build vs. buy comparisons, in-house solutions",
    tag: "Product" as DecisionDriverTag,
  },

  // Implementation & Support
  {
    id: "ease-of-implementation",
    name: "Ease of Implementation",
    description: "Complexity, effort, or timeline to get set up",
    tag: "Implementation" as DecisionDriverTag,
  },
  {
    id: "internal-resources-required",
    name: "Internal Resources Required",
    description: "Lift required from buyer side during onboarding",
    tag: "Implementation" as DecisionDriverTag,
  },
  {
    id: "services-support-quality",
    name: "Services / Support Quality",
    description: "Feedback on onboarding, customer success, and support teams",
    tag: "Implementation" as DecisionDriverTag,
  },
  {
    id: "self-service-vs-services",
    name: "Self-Service vs. Services",
    description: "Preference for DIY tools vs. white-glove onboarding",
    tag: "Implementation" as DecisionDriverTag,
  },
  {
    id: "migration-complexity",
    name: "Migration Complexity",
    description: "Cost or effort to move from existing vendor",
    tag: "Implementation" as DecisionDriverTag,
  },

  // RFP / Process-Specific (using Implementation tag since there's no RFP tag)
  {
    id: "procurement-legal-complexity",
    name: "Procurement / Legal Complexity",
    description: "Red tape, contract review cycles, or process overhead",
    tag: "Implementation" as DecisionDriverTag,
  },
  {
    id: "compliance-rfp-fit",
    name: "Compliance / RFP Fit",
    description: "Fit with checklist-style requirements in RFPs",
    tag: "Implementation" as DecisionDriverTag,
  },

  // Buyer Behavior / Market Factors
  {
    id: "brand-awareness-reputation",
    name: "Brand Awareness / Reputation",
    description: "Whether buyer knew and trusted the brand",
    tag: "Buyer Behavior" as DecisionDriverTag,
  },
  {
    id: "risk-aversion-status-quo",
    name: "Risk Aversion / Status Quo",
    description: "Fear of switching, comfort with existing solution",
    tag: "Buyer Behavior" as DecisionDriverTag,
  },
  {
    id: "referenceability-case-studies",
    name: "Referenceability / Case Studies",
    description: "Whether we had social proof, logos, or relevant examples",
    tag: "Buyer Behavior" as DecisionDriverTag,
  },
  {
    id: "value-realization",
    name: "Value Realization",
    description: "Whether they felt ROI from current or past use (for renewal deals)",
    tag: "Buyer Behavior" as DecisionDriverTag,
  },
  {
    id: "existing-usage-adoption",
    name: "Existing Usage & Adoption",
    description: "Adoption rates or value realized during previous use (renewal/expansion)",
    tag: "Buyer Behavior" as DecisionDriverTag,
  },

  // PLG-Specific Product Decision Drivers (using Product tag)
  {
    id: "time-to-value-ttv",
    name: "Time to Value (TTV)",
    description: "How quickly users saw utility or success after signing up",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "onboarding-experience",
    name: "Onboarding Experience",
    description: "Clarity and intuitiveness of first-time user flows",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "self-service-setup",
    name: "Self-Service Setup",
    description: "Whether users could get started without sales or support involvement",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "pricing-transparency",
    name: "Pricing Transparency",
    description: "Clarity and accessibility of pricing on the website",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "free-tier-trial-value",
    name: "Free Tier / Trial Value",
    description: "Whether the free plan or trial meaningfully showcased core value",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "upgrade-path-conversion-ux",
    name: "Upgrade Path / Conversion UX",
    description: "Ease of moving from free to paid plans",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "in-app-education-guidance",
    name: "In-App Education / Guidance",
    description: "Quality of tooltips, tutorials, walkthroughs, or embedded docs",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "community-ecosystem",
    name: "Community & Ecosystem",
    description: "Availability of community support, plugins, integrations",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "frictionless-collaboration",
    name: "Frictionless Collaboration", 
    description: "Ability to invite and work with team members easily",
    tag: "Product" as DecisionDriverTag,
  },
  {
    id: "usage-based-pricing-fit",
    name: "Usage-Based Pricing Fit",
    description: "Whether pricing scales fairly with usage or seats",
    tag: "Pricing" as DecisionDriverTag,
  },
  {
    id: "integration-simplicity",
    name: "Integration Simplicity",
    description: "Ease of connecting with other tools from within the app",
    tag: "Product" as DecisionDriverTag,
  },
];

export const ROLEPLAY_AVATARS = [
  {
    name: "Sarah",
    icon_path_name: "/roleplay_avatars/sarah_avatar_picture_cropped.jpg",
    voice: "alloy"
  },
  {
    name: "John",
    icon_path_name: "/roleplay_avatars/john_avatar_picture_cropped.jpg",
    voice: "echo"
  },
  {
    name: "Benedict",
    icon_path_name: "/roleplay_avatars/benedict_avatar_picture_cropped.jpg",
    voice: "ash"
  },
];

export const ROLEPLAY_TYPES = [
  { id: "cold-call", name: "Cold Call" },
  { id: "discovery-call", name: "Discovery Call" },
  { id: "demo", name: "Demo" },
];

export const MAX_DEALGRID_DEALS_FOR_PROMPT_STUFFING = 25;

export {
  DATE_RANGE_PRESETS,
  type DateRangePresetKey,
} from "./date-range-presets";

export const CHRON_SCHEDULE_OPTIONS = {
  every_day: {
    label: "Every Day",
    value: "0 0 * * *",
  },
  every_week: {
    label: "Every Week",
    value: "0 0 * * 0",
  },
  every_2_weeks: {
    label: "Every 2 Weeks",
    value: "0 0 */14 * *",
  },
  every_month: {
    label: "Every Month",
    value: "0 0 1 * *",
  },
  every_3_months: {
    label: "Every 3 Months",
    value: "0 0 1 */3 *",
  },
};

export type ChronScheduleOptionKey = keyof typeof CHRON_SCHEDULE_OPTIONS;

// Role options matching the database enum and onboarding form
export const roleOptions = [
  { value: "marketing", label: "Marketing" },
  { value: "product", label: "Product Management" },
  { value: "competitive", label: "Competitive Intelligence" },
  { value: "sales", label: "Sales" },
  { value: "enablement", label: "Enablement" },
  { value: "other", label: "Other" },
];

// Answer Pack type for pre-built answer group templates
export interface AnswerPackTemplate {
  id: string;
  name: string;
  description: string;
  icon: string; // Icon name to use (e.g., "Trophy", "Target", "Users")
  color: string; // Gradient class for the icon backdrop (e.g., "from-amber-400 to-orange-500")
  group_prompt?: string; // Optional shared prompt for the entire group
  answers: {
    name: string;
    prompt: string;
    length: "boolean" | "sentence" | "paragraph" | "select";
    select_options?: string[];
  }[];
}

// Pre-built Answer Packs for users to choose from
export const DEFAULT_ANSWER_PACKS: AnswerPackTemplate[] = [
  {
    id: "meddpicc",
    name: "MEDDPICC",
    description: "Do not let deal stage, deal size, or forecast category influence the classification — this should reflect only what has actually been said or done with respect to this specific MEDDPICC element",
    icon: "ClipboardCheck",
    color: "from-emerald-400 to-teal-500",
    answers: [
      {
        name: "Paper Process",
        prompt: "Classify the rep's understanding of and control over the contracting, legal, and procurement steps required to close, based only on evidence in calls, notes, and emails.\n\nIf the data contains no discussion of contracting, legal, or procurement steps, select \"Unknown.\"",
        length: "select",
        select_options: ["Unknown", "Acknowledged, Not Mapped", "Key Steps and Contacts Known", "Fully Mapped, Proactively Managed", "Rep Owns It, Aligned to Signature"],
      },
      {
        name: "Champion",
        prompt: "Classify the strength and development of the rep's internal advocate, based only on evidence in calls, notes, and emails.\n\nIf the data shows no internal advocate at all, select \"No Champion Identified.\"",
        length: "select",
        select_options: ["No Champion Identified", "Potential Champion, Undeveloped", "Engaged, Sharing Internal Intel", "Actively Selling Internally", "Trusted and Co-Invested"],
      },
      {
        name: "Competition",
        prompt: "Classify the rep's awareness of and strategy against the competitive landscape for this deal, based only on evidence in calls, notes, and emails.\n\nIf the data contains no mention of competitors or alternatives, select \"Ignored or Unknown.\"",
        length: "select",
        select_options: ["Ignored or Unknown", "Named, No Differentiation Strategy", "Differentiators Articulated", "Competitive Traps Set", "Criteria Reframed, Competition Neutralized"],
      },
      {
        name: "Metrics",
        prompt: "Classify how well the rep has quantified business impact and cost of inaction with this buyer, based only on evidence in calls, notes, and emails.\n\nIf there is no evidence either way in the available data, select \"No Metrics Discussed\" and do not infer intent that wasn't stated.",
        length: "select",
        select_options: ["No Metrics Discussed", "Numbers Mentioned, Not Tied to Buyer", "Basic ROI Quantified", "Metrics Co-Created with Buyer", "Buyer-Owned, Exec-Level Urgency"],
      },
      {
        name: "Economic Buyer",
        prompt: "Classify the rep's identification of and access to the person with final budget authority and sign-off, based only on evidence in calls, notes, and emails.\n\nIf there is no evidence of an economic buyer being named anywhere in the data, select \"Not Identified.\"",
        length: "select",
        select_options: ["Not Identified", "Named, Not Engaged", "Identified, Indirect Influence Only", "Engaged and Supportive"],
      },
      {
        name: "Decision Criteria",
        prompt: "Classify how well the rep understands the criteria the buyer will use to evaluate and choose a solution, based only on evidence in calls, notes, and emails.\n\nIf the data shows no discussion of evaluation criteria, select \"Unknown.\"",
        length: "select",
        select_options: ["Unknown", "Assumed, Not Confirmed", "Documented", "Mapped to Our Strengths", "Co-Created, Differentiation Embedded"],
      },
      {
        name: "Decision Process",
        prompt: "Classify the rep's understanding of and involvement in the steps, stakeholders, and timeline required to reach a signed agreement, based only on evidence in calls, notes, and emails.\n\nIf the data shows no discussion of the buyer's internal decision process, select \"Unknown.\"",
        length: "select",
        select_options: ["Unknown", "Vague, Key Steps or Players Missing", "Mapped with Milestones and Stakeholders", "Fully Documented, Rep Guiding", "Rep Orchestrating the Process"],
      },
      {
        name: "Pain",
        prompt: "Classify the depth and validation of the business pain the rep has uncovered, based only on evidence in calls, notes, and emails.\n\nIf the data contains no discussion of a specific business problem, select \"No Pain Identified.\"",
        length: "select",
        select_options: ["No Pain Identified", "Surface-Level, Unvalidated", "Confirmed, Linked to Business Impact", "Quantified, Tied to Senior Stakeholder", "Business-Critical, Economic-Buyer-Owned"],
      },
    ],
  },
  {
    id: "bant",
    name: "BANT",
    description: "Do not let deal stage, deal size, or forecast category influence the classification — this should reflect only what has actually been said or done with respect to this specific BANT element",
    icon: "Target",
    color: "from-violet-400 to-purple-500",
    answers: [
      {
        name: "Budget",
        prompt: "Classify how confirmed the buyer's budget is, and how clear the path to securing it is, based only on evidence in calls, notes, and emails.\n\nIf there is no evidence of budget being discussed anywhere in the data, select \"No Budget Discussion.\"",
        length: "select",
        select_options: ["No Budget Discussion", "Raised, Unconfirmed", "Confirmed at General Level", "Confirmed and Aligned to Deal Size", "Secured or Actively Being Created"],
      },
      {
        name: "Authority",
        prompt: "Classify the rep's identification of and access to the person with actual decision-making power, based only on evidence in calls, notes, and emails.\n\nIf there is no evidence of a decision-maker being named anywhere in the data, select \"Decision-Maker Unknown.\"",
        length: "select",
        select_options: ["Decision-Maker Unknown", "Identified, Not Engaged", "Indirect Access Only", "Engaged and Involved", "Champion with Exec Sponsorship"],
      },
      {
        name: "Need",
        prompt: "Classify the depth and validation of the business need the rep has uncovered, based only on evidence in calls, notes, and emails.\n\nIf the data contains no discussion of a specific business need, select \"No Clear Need Established.\"",
        length: "select",
        select_options: ["No Clear Need Established", "Surface-Level, Unvalidated", "Confirmed, Linked to Business Priority", "Quantified, Tied to Senior Stakeholder", "Critical and Decision-Maker-Owned"],
      },
      {
        name: "Timeline",
        prompt: "Classify how clear and buyer-driven the timeline for the buying decision and go-live is, based only on evidence in calls, notes, and emails.\n\nIf the data contains no discussion of timing or urgency, select \"No Timeline Discussed.\"",
        length: "select",
        select_options: ["No Timeline Discussed", "Vague, Rep-Driven", "Identified, Roughly Aligned to Forecast", "Confirmed with Milestones", "Buyer-Owned, Tied to Business Event"],
      },
    ],
  },
  {
    id: "persona-insights",
    name: "Persona Insights",
    description: "Understand buyer personas and messaging fit",
    icon: "UserCircle",
    color: "from-pink-400 to-rose-500",
    answers: [
      {
        name: "Primary Buyer Persona (EDIT THE OPTIONS!)",
        prompt: "What is the primary buyer persona involved in this deal? Select the role or title that best describes the main buyer.",
        length: "select",
        select_options: ["Executive/C-Suite", "VP/Director", "Manager", "Individual Contributor", "Technical/IT", "Finance/Procurement", "Operations", "Other"],
      },
      {
        name: "Primary Persona Role in Decision",
        prompt: "What role did the primary buyer play in the decision-making process?",
        length: "select",
        select_options: ["Champion", "Economic Buyer", "Decision Maker", "Technical Evaluator", "End User", "Approver", "Blocker", "Influencer"],
      },
      {
        name: "Secondary Buyer Persona (EDIT THE OPTIONS!)",
        prompt: "What is the secondary buyer persona involved in this deal? Select the role or title that best describes the secondary buyer.",
        length: "select",
        select_options: ["Executive/C-Suite", "VP/Director", "Manager", "Individual Contributor", "Technical/IT", "Finance/Procurement", "Operations", "Other"],
      },
      {
        name: "Secondary Persona Role in Decision",
        prompt: "What role did the secondary buyer play in the decision-making process?",
        length: "select",
        select_options: ["Champion", "Economic Buyer", "Decision Maker", "Technical Evaluator", "End User", "Approver", "Blocker", "Influencer"],
      },
    ],
  },
  {
    id: "use-case-insights",
    name: "Use Case, Outcome, and Purchase Trigger",
    description: "Understand what the buyer wants to accomplish, the business result they expect, and what prompted them to act now.",
    icon: "Package",
    color: "from-cyan-400 to-teal-500",
    group_prompt: "Classify the buyer's primary use case, desired business outcome, and purchase trigger using the closest available option. The use case describes how the buyer intends to use the product. The desired outcome describes the business impact they expect. The purchase trigger describes what caused the evaluation to happen now. Base selections on buyer-stated evidence rather than feature requests, generic product benefits, or rep speculation. Select Other when the evidence is clear but no option fits, and Unclear when the available evidence is insufficient.",
    answers: [
      {
        name: "Primary Buyer Use Case (EDIT THESE OPTIONS!)",
        prompt: "Select the option that best describes how the buyer primarily intends to use the product.",
        length: "select",
        select_options: ["Automate a Manual Workflow", "Improve an Existing Workflow", "Enable a New Workflow", "Replace an Existing Solution", "Consolidate Multiple Tools", "Scale an Existing Process", "Centralize Data and Visibility", "Connect Disconnected Systems", "Other", "Unclear"],
      },
      {
        name: "Desired Business Outcome (EDIT THESE OPTIONS!)",
        prompt: "Select the primary business result the buyer expects to achieve.",
        length: "select",
        select_options: ["Increase Revenue", "Reduce Costs", "Save Time and Increase Productivity", "Reduce Business or Operational Risk", "Improve Customer Experience", "Improve Decision-Making and Visibility", "Improve Quality and Consistency", "Meet Security or Compliance Requirements", "Accelerate Time-to-Market or Time-to-Value", "Support Growth and Scale", "Other", "Unclear"],
      },
      {
        name: "Purchase Trigger (EDIT THESE OPTIONS!)",
        prompt: "Select the event or change that most directly prompted the buyer to evaluate a solution now.",
        length: "select",
        select_options: ["Contract Renewal or Replacement Window", "Vendor Failure or Dissatisfaction", "New Leadership or Executive Mandate", "New Strategic Initiative", "Growth or Scaling Pressure", "Cost-Reduction Mandate", "Regulatory or Compliance Deadline", "Security Incident or Risk Event", "Technology Migration or Modernization", "New Product or Market Launch", "Merger, Acquisition, or Reorganization", "Newly Available Budget", "No Clear Trigger", "Other", "Unclear"],
      },
    ],
  }
];

// Character limit for reference assets used when grading metrics
export const MAX_ASSET_CHARACTERS_FOR_METRICS = 12000;

// Character limit for reference assets (playbooks, messaging decks, etc.) linked to a
// play tier and injected into the play-generation prompt. Used both for the UI selector
// budget and the server-side cap in play-surface so the user sees what the model gets.
export const MAX_ASSET_CHARACTERS_FOR_PLAYS = 24000;

// Character limit for reference assets used when generating custom answers
export const MAX_ASSET_CHARACTERS_FOR_ANSWERS = 12000;

export { USAGE_BUCKETS } from "./usage/constants";

// Feature tag types — per-deal labels applied to deal_feature_associations
export type FeatureTagType = {
  id: string;
  label: string;
  color?: string;
  description?: string;
};

// Shared palette for feature tag colors, used both as the picker options in
// settings/features and as the deterministic fallback in getFeatureTagColor.
export const FEATURE_TAG_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#3b82f6", // blue
  "#64748b", // slate
];

export const DEFAULT_FEATURE_TAG_TYPES: FeatureTagType[] = [
  { id: "feature-request", label: "Feature Request", color: "#6366f1", description: "The prospect asked for a capability that doesn't exist yet in your product." },
  { id: "bug", label: "Bug", color: "#ef4444", description: "A defect or broken behavior in your product was raised as a concern during the deal." },
  { id: "competitive-gap", label: "Competitive Gap", color: "#f97316", description: "A competitor offered something your product currently lacks that influenced the decision." },
  { id: "competitive-advantage", label: "Competitive Advantage", color: "#22c55e", description: "A capability where your product outperformed the competition and drove positive sentiment." },
  { id: "tablestakes", label: "Tablestakes", color: "#64748b", description: "A baseline expectation the prospect considers non-negotiable for any vendor in this category." },
  { id: "loved", label: "Loved", color: "#ec4899", description: "A feature the prospect specifically praised or cited as a reason for choosing your product." },
  { id: "usability-issue", label: "Usability Issue", color: "#eab308", description: "A capability exists in your product but the prospect found it difficult, confusing, or time-consuming to use." },
  { id: "security-compliance", label: "Security / Compliance", color: "#8b5cf6", description: "The prospect requires a security certification, access control model, or compliance capability your product doesn't currently meet." },
  { id: "performance-reliability", label: "Performance / Reliability", color: "#3b82f6", description: "The prospect raised concerns about your product's speed, stability, or ability to scale to their usage." },
];

/**
 * Resolves the display color for a feature tag type. Falls back to a
 * deterministic hash of the tag id (stable across renders) rather than a
 * flat gray, so tags saved without an explicit color still look distinct.
 */
export function getFeatureTagColor(tag: Pick<FeatureTagType, "id" | "color">): string {
  if (tag.color) return tag.color;
  let hash = 0;
  for (let i = 0; i < tag.id.length; i++) {
    hash = (hash << 5) - hash + tag.id.charCodeAt(i);
    hash |= 0;
  }
  return FEATURE_TAG_COLORS[Math.abs(hash) % FEATURE_TAG_COLORS.length];
}

// --- Model pricing (generate-insights-orchestrator-2.ts / insight-item-agent.ts) ---

export type ContextTierPricing = {
  inputUsdPerMillion: number;
  cachedInputUsdPerMillion: number;
  outputUsdPerMillion: number;
};

export type ModelPricing = {
  shortContext: ContextTierPricing;
  longContext: ContextTierPricing;
  // Input-token count at which a call is priced at longContext rates instead
  // of shortContext rates.
  longContextThresholdTokens: number;
};

// Defaults from the provider's published rates; PAIGE_AGENT_MODEL_PRICING_JSON
// can override/extend per deployment without a deploy.
export const DEFAULT_MODEL_PRICING: Record<string, ModelPricing> = {
  "gpt-5.4": {
    shortContext: { inputUsdPerMillion: 2.5, cachedInputUsdPerMillion: 0.25, outputUsdPerMillion: 15 },
    longContext: { inputUsdPerMillion: 5, cachedInputUsdPerMillion: 0.5, outputUsdPerMillion: 22.5 },
    longContextThresholdTokens: 272_000,
  },
  // Mini's long-context tier/threshold isn't confirmed in the provider's docs
  // (per Bryce, 2026-07-17) — approximated as 30% of full's long-context rate,
  // matching how mini's short-context rate is exactly 30% of full's, and reusing
  // full's threshold since no mini-specific one is documented. The gpt-5.4-mini
  // runs we're pricing did run under a much larger (~1M token) context window,
  // so mini clearly can go long — this threshold/rate should be corrected once
  // the actual mini long-context pricing is confirmed.
  "gpt-5.4-mini": {
    shortContext: { inputUsdPerMillion: 0.75, cachedInputUsdPerMillion: 0.075, outputUsdPerMillion: 4.5 },
    longContext: { inputUsdPerMillion: 1.5, cachedInputUsdPerMillion: 0.15, outputUsdPerMillion: 6.75 },
    longContextThresholdTokens: 272_000,
  },
};
